import { type ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface Conformity {
  id?: number;
  title?: string;
}

export interface ControlRow {
  controlDate?: string;
  resource?: { name?: string };
  flow?: { name?: string };
  conformity?: Conformity | null;
  reportState?: number | null;
}

interface GenerateColumnsArgs {
  t: (key: string, fallback?: string) => string;
}

function conformityClass(id: number | undefined) {
  if (id === 1) return "bg-emerald-600 text-white hover:bg-emerald-600";
  if (id === 2) return "bg-sky-600 text-white hover:bg-sky-600";
  return "bg-destructive text-white hover:bg-destructive";
}

export default function generateColumns({
  t,
}: GenerateColumnsArgs): ColumnDef<ControlRow>[] {
  return [
    {
      id: "controlDate",
      header: t("dashboard.recent-controls.date"),
      cell: ({ row }) => row.original.controlDate ?? "",
    },
    {
      id: "resourceName",
      header: t("dashboard.recent-controls.resource"),
      cell: ({ row }) => row.original.resource?.name ?? t("common.unknown"),
    },
    {
      id: "flowName",
      header: t("dashboard.recent-controls.flow"),
      cell: ({ row }) => row.original.flow?.name ?? t("common.unknown"),
    },
    {
      id: "conformity",
      header: t("dashboard.recent-controls.conformity"),
      cell: ({ row }) => {
        const value = row.original.conformity;
        if (!value) return "-";
        return (
          <Badge className={cn(conformityClass(value.id))}>
            {value.title}
          </Badge>
        );
      },
    },
    {
      id: "reportState",
      header: t("dashboard.recent-controls.report-state"),
      cell: ({ row }) => {
        const value = row.original.reportState;
        const isAvailable = value === 1;
        return (
          <Badge
            variant="outline"
            className={cn(
              !isAvailable && "border-amber-500 text-amber-600 dark:text-amber-400"
            )}
          >
            {value === 0
              ? t("dashboard.recent-controls.generating")
              : t("dashboard.recent-controls.available")}
          </Badge>
        );
      },
    },
  ];
}
